import {call, put, takeLatest, all} from 'redux-saga/effects'
import * as correiosAPI from '../api/correios'
import {formatDate} from '../../helpers/dateFormat'
import {
  FETCH_SHIPMENTS_REQUEST,
  fetchShipmentsSuccess,
  fetchShipmentsFailure,
  IMPORT_CSV_REQUEST,
  importCsvSuccess,
  importCsvFailure,
  DELETE_SHIPMENTS_REQUEST,
  deleteShipmentsSuccess,
  deleteShipmentsFailure,
  SEND_MESSAGE_REQUEST,
  sendMessageSuccess,
  sendMessageFailure,
  CANCEL_MESSAGE_REQUEST,
  cancelMessageSuccess,
  cancelMessageFailure
} from '../modules/correios'
import {notification} from 'antd'

// Função para mapear os dados da API para o formato esperado pelo componente
const mapCorreiosData = (data) => {
  return data.map(item => ({
    id: item.id,
    nome_aluno: item.student_name,
    cpf: item.document,
    codigo_rastreio: item.tracking_code,
    data_importacao: item.imported_at ? formatDate(item.imported_at) : '-',
    data_envio: item.sent_at ? formatDate(item.sent_at) : '-',
    status: item.status.toLowerCase(),
    template_botmaker: item.template_botmaker,
  }))
}

// Fetch Correios
function* fetchCorreiosSaga(action) {
  try {
    const {page, itemsPerPage, filters, callback} = action

    const response = yield call(correiosAPI.fetchCorreios, page, itemsPerPage, filters)

    const {data, ...pagination} = response

    const mappedData = mapCorreiosData(data)

    yield put({
      type: 'FETCH_CORREIOS_SUCCESS',
      data: mappedData,
      pagination,
    })

    if (callback) {
      callback()
    }
      } catch (error) {
      const {callback} = action
      yield put({
        type: 'FETCH_CORREIOS_FAILURE',
        errorMessage: error.message || 'Erro ao carregar correios',
      })

      if (callback) {
        callback()
      }
    }
}

function* watchFetchCorreios() {
  yield takeLatest('FETCH_CORREIOS_REQUEST', fetchCorreiosSaga)
}

// Download Spreadsheet
function* downloadSpreadsheetSaga(action) {
  try {
    const {filters, callback} = action

    const response = yield call(correiosAPI.downloadSpreadsheet, filters)

    // Criar um blob e fazer download do arquivo
    const blob = new Blob([response], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `correios_${new Date().toISOString().slice(0, 10)}.xlsx`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    if (callback) {
      callback()
    }
  } catch (error) {
    const {callback} = action
    yield put({
      type: 'DOWNLOAD_CORREIOS_SPREADSHEET_FAILURE',
      errorMessage: error.message || 'Erro ao baixar planilha',
    })

    if (callback) {
      callback()
    }
  }
}

function* watchDownloadSpreadsheet() {
  yield takeLatest('DOWNLOAD_CORREIOS_SPREADSHEET_REQUEST', downloadSpreadsheetSaga)
}

// Import CSV (Legacy - removido, agora usando importCsvSaga)
// Mantido apenas watchImportCSV para compatibilidade com ações antigas
function* watchImportCSV() {
  // Redirecionar para o novo saga
  yield takeLatest('IMPORT_CORREIOS_CSV_REQUEST', importCsvSaga)
}

function* fetchShipmentsSaga(action) {
  try {
    const {page, per_page, ...filters} = action.payload;
    const response = yield call(correiosAPI.fetchCorreios, page, per_page, filters);
    yield put(fetchShipmentsSuccess(response));
  } catch (error) {
    yield put(fetchShipmentsFailure(error.message));
  }
}

function* importCsvSaga(action) {
  try {
    const {file, templateBotMaker} = action.payload;
    const response = yield call(correiosAPI.importCSV, file, templateBotMaker);
    
    // Debug: verificar estrutura da resposta
    console.log('Import CSV Response (raw):', response);
    
    // A API retorna dados aninhados: os valores reais estão em response.data
    // Priorizar dados aninhados quando disponíveis
    const dataSource = response.data || response;
    
    // Garantir que os dados estejam na estrutura esperada pela modal
    const processedResponse = {
      // Priorizar dados de response.data, com fallback para response raiz
      imported: dataSource.imported || response.imported || dataSource.success_count || response.success_count || 0,
      errors: dataSource.errors || response.errors || dataSource.error_count || response.error_count || 0,
      duplicates: dataSource.duplicates || response.duplicates || dataSource.duplicate_count || response.duplicate_count || 0,
      total: dataSource.total || response.total || dataSource.total_count || response.total_count || 0,
      error_details: dataSource.error_details || response.error_details || dataSource.errors_details || response.errors_details || [],
      duplicate_details: dataSource.duplicate_details || response.duplicate_details || dataSource.duplicates_details || response.duplicates_details || [],
      
      // Preservar metadados importantes da resposta raiz
      success: response.success,
      message: response.message,
      
      // Manter dados originais para debug
      raw_response: response
    };
    
    console.log('Processed Import Response:', processedResponse);
    
    yield put(importCsvSuccess(processedResponse));
    
    // Notificações removidas - apenas a modal de resultados será mostrada
    
  } catch (error) {
    yield put(importCsvFailure(error.message));
    
    notification.error({
      message: 'Erro na importação',
      description: 'Erro ao importar arquivo CSV: ' + error.message,
    });
  }
}

function* deleteShipmentsSaga(action) {
  try {
    const response = yield call(correiosAPI.deleteShipments, action.payload);
    yield put(deleteShipmentsSuccess(response));
    
    notification.success({
      message: 'Sucesso',
      description: 'Kits deletados com sucesso!',
    });
  } catch (error) {
    yield put(deleteShipmentsFailure(error.message));
    
    notification.error({
      message: 'Erro',
      description: 'Erro ao deletar kits: ' + error.message,
    });
  }
}

function* sendMessageSaga(action) {
  try {
    const response = yield call(correiosAPI.sendMessage, action.payload);
    yield put(sendMessageSuccess(response));
    
    notification.success({
      message: 'Sucesso',
      description: 'Mensagens disparadas com sucesso pelo BotMaker!',
    });
  } catch (error) {
    yield put(sendMessageFailure(error.message));
    
    notification.error({
      message: 'Erro',
      description: 'Erro ao disparar mensagens: ' + error.message,
    });
  }
}

function* cancelMessageSaga(action) {
  try {
    const response = yield call(correiosAPI.cancelMessage, action.payload);
    yield put(cancelMessageSuccess(response));
    
    notification.success({
      message: 'Sucesso',
      description: 'Mensagens desagendadas com sucesso!',
    });
  } catch (error) {
    yield put(cancelMessageFailure(error.message));
    
    notification.error({
      message: 'Erro',
      description: 'Erro ao desagendar mensagens: ' + error.message,
    });
  }
}

// Main saga
export default function* correiosSaga() {
  yield all([
    watchFetchCorreios(),
    watchDownloadSpreadsheet(),
    watchImportCSV(),
    takeLatest(FETCH_SHIPMENTS_REQUEST, fetchShipmentsSaga),
    takeLatest(IMPORT_CSV_REQUEST, importCsvSaga),
    takeLatest(DELETE_SHIPMENTS_REQUEST, deleteShipmentsSaga),
    takeLatest(SEND_MESSAGE_REQUEST, sendMessageSaga),
    takeLatest(CANCEL_MESSAGE_REQUEST, cancelMessageSaga)
  ])
} 